import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Spin,
  Typography,
  Flex,
  Divider,
  message,
  Tooltip,
} from "antd";
import { forwardRef, useState, useImperativeHandle } from "react";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import EnhancedTable from "@/hocComponents/EnhancedTable";

/** ===== lazy select ===== */
const useLazySelect = (apiUrl, key, mapper) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await http.get(apiUrl);
      if (res?.code === "1") {
        const list = res?.data?.[key] || res?.data || [];
        setOptions(mapper(list));
      } else {
        setOptions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return { options, loading, load };
};

const ChangeTrustModal = forwardRef(({ onSuccess }, ref) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [glAcctList, setGlAcctList] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [propIdList, setPropIdList] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [tableData, setTableData] = useState([]);

  const filterOption = (input, option) => {
    const keyword = String(input || "").trim().toLowerCase();
    if (!keyword) return true;
    const label = String(option?.label || "").toLowerCase();
    return label.includes(keyword);
  };

  /** ===== Trust 下拉 ===== */
  const trustSelect = useLazySelect(api.getTrustDictList, "trust", (data) =>
    data.map((item) => ({
      label: item?.trustNameTitle || "",
      value: String(item?.trustId),
    })),
  );

  /** ===== 打开 Modal ===== */
  useImperativeHandle(ref, () => ({
    setModelAdd: async (record) => {
      setVisible(true);
      form.resetFields();
      setShowResult(false);
      setTableData([]);
      setOpenLoading(true);
      setCurrentRecord(record || {});
      try {
        const resDate = await http.get(api.commonCurrentDate);

        trustSelect.load();
      } finally {
        setOpenLoading(false);
      }
    },
  }));

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setShowResult(false);
    setTableData([]);
  };

  /** ===== bank 联动 ===== */
  const handleTrustChange = async (trustId) => {
    resetResult();
    form.setFieldsValue({
      arBankId: undefined,
      apBankId: undefined,
    });
    await loadBankOptions(trustId);
  };

  const loadBankOptions = async (trustId) => {
    const id = String(trustId || "").trim();
    if (!id) {
      setBankOptions([]);
      return;
    }

    setBankLoading(true);
    try {
      const res = await http.get(api.getBankListDict, {
        params: { trustId: id },
      });

      if (res?.code !== "1") {
        setBankOptions([]);
        return;
      }

      const list = Array.isArray(res?.data) ? res.data : [];

      setBankOptions(
        list.map((i) => ({
          label: i.name,
          value: String(i.bankId),
        })),
      );
    } catch (err) {
      console.error("bank load error:", err);
      setBankOptions([]);
    } finally {
      setBankLoading(false);
    }
  };

  const resetResult = () => {
    setShowResult(false);
    setTableData([]);
  };

  /** ===== 查询按钮 ===== */
  const handleQuery = async () => {
    try {
      const values = await form.validateFields([
        "trustId",
        "arBankId",
        "apBankId",
      ]);

      setQueryLoading(true);

      const params = {
        propId: currentRecord?.propId,
        trustId: values.trustId,
        arBankId: values.arBankId,
        apBankId: values.apBankId,
      };

      const resData = await http.post(api.getChangeTrustResult, params);

      if (resData?.code !== "1") {
        message.warning(resData?.msg);
        return;
      }

      // 确保数据完整，防止 undefined
      const list = (resData.data || []).map((item, index) => ({
        key: index,
        prop: item?.prop ?? "-",
        glAcct: item?.glAcct ?? "-",
        date: item?.date ?? null,
        amount: item?.amount ?? 0,
        remark: item?.remark ?? "-",
      }));

      setTableData(list);
      setShowResult(true);
      getGlAcctListFn();
      getPropIdFn();
    } catch (error) {
      console.error("Query error:", error);
      message.error("Failed to query data");
    } finally {
      setQueryLoading(false);
    }
  };

  const getGlAcctListFn = async () => {
    try {
      const response = await http.get(api.getAcctDictList, {
        params: { glChartNameId: "1" },
      });

      const list = (response.data || []).map((item) => ({
        value: item.glAcct,
        label: item.glAcctTitle,
      }));
      setGlAcctList(list);
    } catch (error) {
      console.error("Failed to load GL Acct list:", error);
    }
  };

  const getPropIdFn = async (content = "") => {
    try {
      const response = await http.get(api.getPropSearch, { params: { content } });
      if (response.code === "1") {
        const list = (response.data || []).map((item) => ({
          value: item.propId,
          label: item.propName,
        }));
        setPropIdList(list);
      }
    } catch (error) {
      console.error("Failed to load Prop list:", error);
    }
  };

  /** ===== 最终提交 ===== */
  const submitFn = async () => {
    try {
      const values = await form.validateFields();

      setSubmitLoading(true);

      const params = {
        propId: currentRecord?.propId,
        trustId: values.trustId,
        arBankId: values.arBankId,
        apBankId: values.apBankId,
      };

      const res = await http.post(api.getPropChangeTrust, params);

      if (res?.code === "1") {
        message.success("Success");
        handleCancel();
        onSuccess?.();
      } else {
        commonFn.modalFailedFn(res);
      }
    } catch (error) {
      console.error("Submit error:", error);
      message.error("Failed to submit");
    } finally {
      setSubmitLoading(false);
    }
  };


  const tableColumns = [
    {
      title: "Prop",
      dataIndex: "prop",
      key: "prop",
      width: 200,
        align: "left",
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 180,
            }}
          >
            {text ?? "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "GL Acct",
      dataIndex: "glAcct",
      key: "glAcct",
      width: 200,
     align: "left",
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 180,
            }}
          >
            {text ?? "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
       align: "left",
      render: (text) => {
        if (!text) return "-";
        if (Array.isArray(text)) {
          return dayjs([text[0], text[1] - 1, text[2]]).format("MM/DD/YYYY");
        }
        return dayjs(text).format("MM/DD/YYYY");
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 180,
     align: "left",
      render: (text) => commonFn.amountToDollarBracketFn(text ?? 0),
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      width: 250,
      align: "left",
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 220,
            }}
          >
            {text ?? "-"}
          </div>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      width="50%"
      onCancel={handleCancel}
      maskClosable={false}
      title={
        <Flex className="model_title">
          <EditOutlined className="model_title_mr10" />
          <Typography.Title level={4} className="mg">
            CHANGE TRUST
          </Typography.Title>
        </Flex>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          {showResult && (
            <>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" loading={submitLoading} onClick={submitFn}>
                Submit
              </Button>
            </>
          )}
        </div>
      }
    >
      <Flex>
        <div className="model_top" style={{ width: "100%" }}>
          <div>
            <Spin spinning={openLoading}>
              <Form
                form={form}
                layout="horizontal"
                labelCol={{ style: { width: "200px" } }}
                wrapperCol={{ style: { flex: 1 } }}
                style={{ margin: "20px auto" }}
              >

                {/* Trust */}
                <Form.Item
                  name="trustId"
                  label="Trust"
                  rules={[{ required: true }]}
                  style={{ flex: 1, marginRight: 16 }}
                >
                  <Select
                    options={trustSelect.options}
                    loading={trustSelect.loading}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    filterOption={filterOption}
                    onDropdownVisibleChange={(open) =>
                      open && trustSelect.load()
                    }
                    onChange={handleTrustChange}
                  />
                </Form.Item>

                {/* AR Bank */}
                <Form.Item
                  name="arBankId"
                  label="AR Default Bank"
                  rules={[{ required: true }]}
                  style={{ flex: 1, marginRight: 16 }}
                >
                  <Select
                    options={bankOptions}
                    loading={bankLoading}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    filterOption={filterOption}
                    onChange={resetResult}
                  />
                </Form.Item>

                {/* AP Bank */}
                <Form.Item
                  name="apBankId"
                  label="AP Default Bank"
                  rules={[{ required: true }]}
                  style={{ flex: 1, marginRight: 16 }}
                >
                  <Select
                    options={bankOptions}
                    loading={bankLoading}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    filterOption={filterOption}
                    onChange={resetResult}
                  />
                </Form.Item>

                {/* Submit Button */}
                <Form.Item
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 16,
                  }}
                >
                  {!showResult && (
                    <Button
                      type="primary"
                      loading={queryLoading}
                      onClick={handleQuery}
                    >
                      Submit
                    </Button>
                  )}
                </Form.Item>
              </Form>

              {/* ===== EnhancedTable 展示区域 ===== */}
              {showResult && tableData.length > 0 && (
                <>
                  <Divider />
                  <div style={{ maxHeight: 600,}}>
                    <EnhancedTable
                      columns={tableColumns}
                      dataSource={tableData}
                      loading={false}
                      rowKey="key"
                      pagination={false}
                      scroll={{ x: "max-content", y: 560 }}
                       size="small" 
                    />
                  </div>
                </>
              )}
            </Spin>
          </div>
        </div>
      </Flex>
    </Modal>
  );
});

export default ChangeTrustModal;